import {
  Button,
  Popover,
  PopoverDisclosure,
  PopoverProvider,
  useDialogStore,
  usePopoverStore,
} from "@ariakit/react";
import { useMemo } from "react";
import { useRouter } from "next/navigation";

import { Avatar } from "~/common/components/avatar";
import { Dialog } from "~/common/components/dialog";
import { IconClick } from "~/common/components/icons/click";
import { useCurrentUser } from "~/auth/queries/get-current-user-query";
import { DataSourceList } from "~/data-sources/components/data-source-list";
import { useDataSources } from "~/data-sources/queries/get-data-sources-query";
import { Pathnames } from "~/common/utils/constants";
import { IconShieldCheck } from "~/common/components/icons/shield-check";
import { ThemeMenu } from "~/common/components/theme-menu";
import { useAppTheme } from "~/common/hooks/dom/use-app-theme";

import { useCurrentUserRoles } from "~/auth/queries/get-current-user-roles-query";
import { AuthUtils } from "~/auth/utils/auth-utils";
import { userInfoStyle } from "./user-info.style";

type UserInfoProps = {
  selectedWorkspaceId?: number;
};

export function UserInfo({ selectedWorkspaceId }: UserInfoProps) {
  const dialogStore = useDialogStore();
  const popoverStore = usePopoverStore();

  const router = useRouter();

  const { data: userInfo } = useCurrentUser();

  const { data: userRoles } = useCurrentUserRoles();

  const { data: dataSources } = useDataSources({
    workspaceId: selectedWorkspaceId,
  });

  const connectedCount = useMemo(
    () => dataSources?.filter((x) => x.isConnected).length,
    [dataSources],
  );

  const showWarning = dataSources?.length !== connectedCount;
  const isAdmin = AuthUtils.isAdmin(userRoles);

  function handleAppsClick() {
    popoverStore.setOpen(false);
    dialogStore.setOpen(true);
  }

  function handleAdminClick() {
    popoverStore.setOpen(false);
    router.push(`${Pathnames.ADMIN}`);
  }

  const appTheme = useAppTheme();

  return (
    <PopoverProvider store={popoverStore}>
      <PopoverDisclosure
        className={userInfoStyle.triggerButton()}
        data-test="profile-menu-trigger"
      >
        <Avatar nameSurname={userInfo.displayName} />
        <span className={userInfoStyle.username()}>{userInfo.displayName}</span>
        {!!showWarning && (
          <span className={userInfoStyle.warningContainer()}>
            <span className={userInfoStyle.warningIcon()} />
          </span>
        )}
      </PopoverDisclosure>
      <Popover>
        <div
          className={userInfoStyle.popoverContainer({
            theme: appTheme as "light",
          })}
        >
          {!!selectedWorkspaceId && (
            <Button
              className={userInfoStyle.popoverItemButton()}
              onClick={handleAppsClick}
              data-test="menu-item-connected-apps"
            >
              <IconClick color="currentColor" />
              <span className={userInfoStyle.popoverItemButtonText()}>
                Connected Apps {!!selectedWorkspaceId && `(${connectedCount})`}
              </span>
              {!!showWarning && (
                <span className={userInfoStyle.warningContainer()}>
                  <span className={userInfoStyle.warningIcon()} />
                </span>
              )}
            </Button>
          )}

          {!!isAdmin && (
            <Button
              className={userInfoStyle.popoverItemButton()}
              onClick={handleAdminClick}
            >
              <IconShieldCheck color="currentColor" />
              <span className={userInfoStyle.popoverItemButtonText()}>
                Admin Portal
              </span>
            </Button>
          )}

          <ThemeMenu parentPopoverStore={popoverStore} />

          <hr className={userInfoStyle.divider()} />

          <div
            className={userInfoStyle.popoverItem()}
            data-test="menu-item-profile"
          >
            <div className={userInfoStyle.userInfoContainer()}>
              <Avatar nameSurname={userInfo.displayName} />
              <div className={userInfoStyle.userInfoDetails()}>
                <span className={userInfoStyle.userName()}>
                  {userInfo.displayName}
                </span>
                <span className={userInfoStyle.userEmail()}>
                  {userInfo.email}
                </span>
              </div>
            </div>
          </div>
        </div>

        <Dialog
          id="connected-apps"
          store={dialogStore}
          heading="Connected Apps"
          showCancelButton={false}
          buttonStyle="full"
        >
          <DataSourceList sources={dataSources || []} />
        </Dialog>
      </Popover>
    </PopoverProvider>
  );
}
